import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CheckIcon from '@mui/icons-material/Check';

import clsx from 'clsx';
import { format } from 'date-fns';

import { ReactComponent as PlanProfiIcon } from '../../../../assets/icons/subscriptions/plan-profi.svg';
import { ReactComponent as PlanStarterIcon } from '../../../../assets/icons/subscriptions/plan-starter.svg';
import { ReactComponent as PlanTrainerIcon } from '../../../../assets/icons/subscriptions/plan-trainer.svg';
import AppButton from '../../../../components/designe/buttons/app-button/AppButton';
import { SubscriptionScope } from '../../../../shared/constants';
import { ISubscriptionPlan } from '../../../../types/interfaces/subscription-plan';
import { ISubscriptionRestriction } from '../../../../types/interfaces/subscription-restriction';
import { IUserSubscription } from '../../../../types/interfaces/userSubscription';
import { getDateFnsLocale } from '../../../../utils/datetime/date-time-utils';
import ConfirmDialog from '../../confirm-dialog/ConfirmDialog';
import { useStyles } from './PlanItem.styles';

const PLAN_SCOPE_ICONS: Record<
  SubscriptionScope,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  [SubscriptionScope.starter]: PlanStarterIcon,
  [SubscriptionScope.pro]: PlanProfiIcon,
  [SubscriptionScope.trainer]: PlanTrainerIcon,
};

interface PlanItemProps {
  plan: ISubscriptionPlan;
  currentPlan?: IUserSubscription;
  savePercent?: number | null;
  onClick: (plan: ISubscriptionPlan) => void;
  isUserPlan?: boolean;
  restrictions: ISubscriptionRestriction[];
  /** Loading state while subscription API request is in flight. */
  loading?: boolean;
}

export default function PlanItem({
  plan,
  savePercent,
  onClick,
  isUserPlan,
  restrictions,
  currentPlan,
  loading = false,
}: PlanItemProps) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t, i18n } = useTranslation();
  const formattedSavePercent =
    savePercent != null
      ? new Intl.NumberFormat(i18n.language, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(savePercent)
      : null;

  const [cancelPlanConfirmOpen, setCancelPlanConfirmOpen] = useState<
    'cancel' | 'change' | null
  >(null);

  const PlanIcon = PLAN_SCOPE_ICONS[plan.scope];

  function buttonClicked() {
    if (
      currentPlan?.period === plan.period &&
      (currentPlan?.type as any) === plan.scope
    ) {
      setCancelPlanConfirmOpen('cancel');
    } else if (currentPlan) {
      setCancelPlanConfirmOpen('change');
    } else {
      onClick(plan);
    }
  }

  return (
    <div
      className={clsx(
        classes.planItem,
        currentPlan?.period === plan.period &&
          (currentPlan?.type as any) === plan.scope &&
          classes.planItemActive,
      )}>
      {savePercent != null && (
        <div className={classes.saveMoneyWrapper}>
          <Typography
            variant="success-wrapped-md"
            className={classes.saveMoneyText}>
            -{formattedSavePercent}%
          </Typography>
        </div>
      )}
      <div className={classes.planHeader}>
        {PlanIcon && (
          <div className={classes.planIconBox} aria-hidden>
            <PlanIcon className={classes.planIconSvg} />
          </div>
        )}
        <Typography variant="h2" component="h2" className={classes.title}>
          {t(`choosePlan.scope_${plan.scope}`)}
        </Typography>
      </div>
      <div className={classes.restrictionsWrapper}>
        {restrictions.map((restriction) => (
          <div key={restriction.id} className={classes.restrictionRow}>
            <CheckIcon className={classes.restrictionCheck} fontSize="small" />
            <Typography
              variant="text-sm"
              component="span"
              className={classes.restrictionText}>
              {!restriction.maxValue && `${t('choosePlan.unlimited')} `}
              {restriction.maxValue && `${t('choosePlan.upTo')} `}
              {restriction.maxValue && `${restriction.maxValue} `}
              {t(`choosePlan.restriction_${restriction.restrictionScope}`)}
            </Typography>
          </div>
        ))}
      </div>

      <div className={classes.priceRow}>
        <Typography component="span" className={classes.priceAmount}>
          {plan.price}$
        </Typography>
        <Typography component="span" className={classes.pricePeriod}>
          /{t(`choosePlan.period_${plan.period}`)}
        </Typography>
      </div>
      <div className={classes.bottom}>
        <AppButton
          disabled={isUserPlan || loading}
          loading={loading}
          variant="contained"
          color={
            currentPlan?.period === plan.period &&
            (currentPlan?.type as any) === plan.scope
              ? 'error'
              : 'primary'
          }
          onClick={() => buttonClicked()}>
          {currentPlan && (
            <React.Fragment>
              {currentPlan?.period === plan.period &&
              (currentPlan?.type as any) === plan.scope
                ? t('choosePlan.cancelPlan')
                : t('choosePlan.changePlan')}
            </React.Fragment>
          )}

          {!currentPlan && t('choosePlan.selectPlan')}
        </AppButton>
      </div>

      <ConfirmDialog
        open={cancelPlanConfirmOpen}
        onClose={() => setCancelPlanConfirmOpen(null)}
        title={
          cancelPlanConfirmOpen === 'cancel'
            ? t('choosePlan.cancelPlan')
            : t('choosePlan.changePlan')
        }
        onSubmit={() => {
          setCancelPlanConfirmOpen(null);
          onClick(plan);
        }}>
        {cancelPlanConfirmOpen === 'cancel'
          ? currentPlan?.currentPeriodEnd
            ? t('choosePlan.cancelPlanQuestionWithEndDate', {
                date: format(new Date(currentPlan.currentPeriodEnd), 'PPP', {
                  locale: getDateFnsLocale(i18n.language),
                }),
              })
            : t('choosePlan.cancelPlanQuestionWithoutEndDate')
          : t('choosePlan.changePlanQuestion')}
      </ConfirmDialog>
    </div>
  );
}
